/**
 * JoinClubhouseCTA — compact membership conversion card.
 *
 * Designed for placement at the bottom of long-form content pages
 * (episode detail, guest detail, etc.) where a reader has just consumed
 * content and is primed to convert.
 *
 * Props:
 *   - variant: 'episode' | 'guest' | 'generic'  (default: 'generic')
 *   - testId:  override the data-testid (default depends on variant)
 *
 * Keep this component lean — heavy membership benefits live in MembershipCTACMS
 * on the homepage. This is the "soft ask" inline counterpart.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users } from 'lucide-react';
import './JoinClubhouseCTA.css';

const COPY = {
  episode: {
    eyebrow: "ENJOYED THIS EPISODE?",
    heading: "Join the Clubhouse",
    body: "Get weekly golf insights, member-only episodes, handicap tracking, leaderboards, and giveaways. It's free to join.",
  },
  guest: {
    eyebrow: "LIKED MEETING THIS GUEST?",
    heading: "Join the Clubhouse",
    body: "Members get early access to new episodes, exclusive interviews, and golf community perks. Free to join.",
  },
  generic: {
    eyebrow: "BECOME A MEMBER",
    heading: "Join the Clubhouse",
    body: "Weekly golf insights, member-only content, handicap tracking, and a community of golf-obsessed friends.",
  },
};

export const JoinClubhouseCTA = ({ variant = 'generic', testId }) => {
  const copy = COPY[variant] || COPY.generic;
  const dataTestId = testId || `join-clubhouse-cta-${variant}`;

  return (
    <section
      className="join-clubhouse-cta"
      data-testid={dataTestId}
      aria-labelledby="join-clubhouse-heading"
    >
      <div className="join-clubhouse-cta-inner">
        <div className="join-clubhouse-icon" aria-hidden="true">
          <Users size={28} strokeWidth={2.25} />
        </div>

        <div className="join-clubhouse-text">
          <span className="join-clubhouse-eyebrow">{copy.eyebrow}</span>
          <h2 id="join-clubhouse-heading" className="join-clubhouse-heading">
            {copy.heading}
          </h2>
          <p className="join-clubhouse-body">{copy.body}</p>
        </div>

        <Link
          to="/clubhouse/register"
          className="join-clubhouse-button"
          data-testid={`${dataTestId}-button`}
        >
          <span>Join Free</span>
          <ArrowRight size={18} />
        </Link>
      </div>
    </section>
  );
};

export default JoinClubhouseCTA;
